import CloseIcon from '@material-ui/icons/Close';
import React, { Suspense, useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { appendCdnToImagePath } from '../../common/utils/appendCdnToImagePath';
import { Box, DialogActions, DialogTitle, IconButton, Typography } from '@material-ui/core';
import { clearUserSessionTimers, useActivateUserSessionTimers } from '../../common/utils/userSessionTimers';
import { GAActionMappings, GACategoryMappings, GAPageViewMappings, GARouteMappings } from '../../constants/gaMappings';
import { ItemStatus } from '../../store/interfaces';
import { sendEventsDetails, sendGeneralContentVideoDetails, sendPageViewDetails } from '../../common/utils/gaEvents';
import { Skeleton } from '@material-ui/lab';
import { StyledButton } from '../../common/components/Button';
import { TaskItemStatus } from '../../taskList/constants';
import { useBasicDetails } from '../../store';
import { useLocation } from 'react-router-dom';
import { VideoTaskConfiguration } from '../interfaces/videoTask';
import { VideoTaskContentStyles } from '../styles/videoTaskContent';

const VideoPlayer = React.lazy(() => import('../../common/components/VideoPlayer'));

export interface VideoTaskTitleProps {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const VideoTaskTitle = (props: VideoTaskTitleProps): JSX.Element => {
  const { children, onClose, ...rest } = props;
  const classes = VideoTaskContentStyles({});

  return (
    <DialogTitle disableTypography className={classes.title} {...rest}>
      <Typography variant="h3">{children}</Typography>
      {onClose && (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      )}
    </DialogTitle>
  );
};

export interface VideoTaskContentProps {
  getNextWorkflowItem?(): Promise<void>;
  videoTaskConfiguration: VideoTaskConfiguration;
  goBack(): void;
  open: boolean;
}

const VideoTaskContent = (props: VideoTaskContentProps): JSX.Element => {
  const { getNextWorkflowItem, videoTaskConfiguration, goBack } = props;
  const location = useLocation<{ taskTitle: string; status: ItemStatus }>();
  const taskTitle = location.state && location.state.taskTitle;
  const taskStatus = location.state && location.state.status;
  const videoUrl =
    typeof videoTaskConfiguration.videoUrl === 'string'
      ? videoTaskConfiguration.videoUrl
      : appendCdnToImagePath(videoTaskConfiguration.videoUrl.path);

  const [loading, setLoading] = useState<boolean>(false);
  const [isVideoWatched, setIsVideoWatched] = useState<boolean>(false);

  useEffect(() => {
    sendPageViewDetails(GARouteMappings.EXPVIDEOTASK, GAPageViewMappings.EXPVIDEOTASK);
  }, []);

  const [pageStartTime, setPageStartTime] = useState<Date>(new Date());
  /** Video Callback methods */
  const { userSessionActive } = useBasicDetails();
  const activateUserSessionTimers = useActivateUserSessionTimers();

  const onVideoPlay = (currentRef: React.RefObject<ReactPlayer>): void => {
    console.log('VideoPlayed:', currentRef.current?.getCurrentTime());
    if (userSessionActive) {
      clearUserSessionTimers();
      activateUserSessionTimers();
    }
  };

  const onVideoPause = (currentRef: React.RefObject<ReactPlayer>, url: string): void => {
    console.log('VideoPaused:', currentRef.current?.getCurrentTime(), currentRef.current);
    const currentDuration = currentRef.current?.getCurrentTime();
    const totalDuration = currentRef.current?.getDuration();
    /** GA Event on Video pause */
    sendGeneralContentVideoDetails(currentDuration, totalDuration, pageStartTime, url, '');
  };

  const getTotalVideoDuration = (currentRef: React.RefObject<ReactPlayer>): void => {
    console.log('TotalVideoDuration', currentRef.current?.getDuration());
  };

  const getVideoEnd = (currentRef: React.RefObject<ReactPlayer>, url: string): void => {
    console.log('Video Ended:', currentRef.current?.getCurrentTime());
    const currentDuration = currentRef.current?.getCurrentTime();
    const totalDuration = currentRef.current?.getDuration();
    /** GA Event : on video end */
    sendGeneralContentVideoDetails(currentDuration, totalDuration, pageStartTime, url, '');
    setIsVideoWatched(true);
  };

  /** Method to save current and get next workflow item */
  const saveAndGetNextWorkflowItem = async (): Promise<void> => {
    // sending ga event
    let action = GAActionMappings.EXPVIDEOTASK;
    action = `${action} : Video`;
    sendEventsDetails(GACategoryMappings.EXPVIDEOTASK, action, pageStartTime);

    if (getNextWorkflowItem) {
      setLoading(true);
      await getNextWorkflowItem();
      setLoading(false);
      setPageStartTime(new Date());
    }
  };

  return (
    <>
      <VideoTaskTitle id="dialog-title" onClose={goBack}>
        {taskTitle}
      </VideoTaskTitle>
      <Suspense fallback={<Skeleton height="100vh" />}>
        {props.open && (
          <VideoPlayer
            url={videoUrl}
            lightMode={true}
            onVideoPlay={onVideoPlay}
            onVideoPause={onVideoPause}
            getTotalVideoDuration={getTotalVideoDuration}
            getVideoEnd={getVideoEnd}
          />
        )}
      </Suspense>
      <DialogActions>
        <Box p={1} width="100%" textAlign="center">
          <StyledButton
            buttonText={videoTaskConfiguration.buttonText}
            buttonStyle={videoTaskConfiguration.buttonStyle}
            disabled={
              loading ||
              (taskStatus !== TaskItemStatus.COMPLETED && videoTaskConfiguration.forceWatchVideo && !isVideoWatched)
            }
            onClickHandler={saveAndGetNextWorkflowItem}
          />
        </Box>
      </DialogActions>
    </>
  );
};

export default VideoTaskContent;
